import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Scroll from 'react-scroll';

import useIsMobile from 'hooks/useIsMobile';
import { useAppDispatch, useAppSelector } from 'redux/reduxHooks';

import { LOADING_STATUS, MOBILE_OS } from 'utils/constants/common';
import { getMobileOS } from 'utils/getMobileOS';

import styles from './SignUp.module.scss';
import moment from 'moment';
import DentalButton from 'components/Buttons/DentalButton/DentalButton';

import { batch } from 'react-redux';
import { resetSysType } from 'redux/authSlice';
import FormTextInput from 'components/BookingForm/FormTextInput/FormTextInput';
import PhoneInput from 'components/BookingForm/FormTextInput/PhoneInput/PhoneInput';
import EmailInput from 'components/BookingForm/FormTextInput/EmailInput/EmailInput';
import { geCountryByPhoneNumber } from 'utils/formatPhoneNumber';
import { firstNameValidation, lastNameValidation } from 'utils/inputValidation';
import MobileDatePicker from 'components/BookingForm/Step1/MobileDatePicker/MobileDatePicker';
import { STEPS, SignUp, popFamilyStep, pushFamilyStep, resetFBStep2SignUp, updateContactUser, updateFBStep2 } from 'redux/familyBookingSlice';

const { VERIFICATION } = STEPS;

const SignUpForm = () => {
  const {
    authSlice: { status, socialData },
    familyBookingSlice: {
      step2: { signUp },
    },
  } = useAppSelector((state) => state);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const dispatch = useAppDispatch();

  const { control, formState, getValues, trigger, clearErrors, setValue } =
    useForm({
      mode: 'all',
      defaultValues: {
        email: socialData?.email ?? '',
        firstName: socialData?.firstName ?? '',
        lastName: socialData?.lastName ?? '',
        dob: socialData?.dob ?? '',
        phoneNumber: socialData?.phoneNumber ?? '',
      },
    });

  const isMobile = useIsMobile();

  const [isEmailChecking, setIsEmailChecking] = useState(false);

  const onScrollToElement = (elementName: string) => {
    if (!isMobile) return;
    const extraHeight = -80;

    let headerHeight = -80;

    const mobileOS = getMobileOS();

    if (mobileOS === MOBILE_OS.ANDROID || mobileOS === MOBILE_OS.OTHER) {
      headerHeight =
        -(document.getElementById('main-header')?.offsetHeight || 0) +
        extraHeight;
    }

    Scroll.scroller.scrollTo(elementName, {
      duration: 500,
      delay: 0,
      smooth: true,
      offset: headerHeight,
    });
  };

  const handleClickOnBack = () => {
    dispatch(resetSysType());

    batch(() => {
      dispatch(popFamilyStep());
      dispatch(resetFBStep2SignUp());
    });
  };

  const getFormData = () => {
    const email = getValues('email');
    const firstName = getValues('firstName');
    const lastName = getValues('lastName');
    const dob = getValues('dob');
    const phoneNumber = getValues('phoneNumber');

    const formData = {
      email,
      firstName,
      lastName,
      dob,
      phoneNumber,
      countryCode: signUp.countryCode || geCountryByPhoneNumber(phoneNumber),
    };
    return formData;
  };

  const handleOnSubmit = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    const signUp = getFormData();

    const contactUser = {
      email: signUp.email,
      dob: signUp.dob,
      phoneNumber: signUp.phoneNumber,
      firstName: signUp.firstName,
      lastName: signUp.lastName,
    };

    batch(() => {
      dispatch(updateFBStep2({ signUp }));
      dispatch(updateContactUser(contactUser));
      dispatch(pushFamilyStep(VERIFICATION));
    });
  };

  return (
    <>
      <MobileDatePicker
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onUpdated={(newValue) => {
          setValue('dob', `${newValue.month}/${newValue.day}/${newValue.year}`);
          clearErrors('dob');
        }}
        {...(getValues('dob') && {
          day: moment(getValues('dob')).format('DD'),
          month: moment(getValues('dob')).format('MM'),
          year: moment(getValues('dob')).format('YYYY'),
        })}
      />
      <form className={styles['container']} noValidate>
        <div className={styles['body']}>
          <div>
            <EmailInput
              control={control}
              trigger={trigger}
              isEmailChecking={isEmailChecking}
              setIsEmailChecking={setIsEmailChecking}
            />
            <div className={styles['body-group']}>
              <FormTextInput
                autoComplete="given-name"
                type="text"
                name="firstName"
                label="Patient's First Name"
                control={control}
                placeholder="Input your first name"
                required="This field is required"
                rules={{
                  validate: (value: string) => {
                    const message = firstNameValidation(value);
                    return message || true;
                  },
                }}
              />
              <FormTextInput
                autoComplete="family-name"
                name="lastName"
                label="Patient's Last Name"
                control={control}
                placeholder="Input your last name"
                required="This field is required"
                rules={{
                  validate: (value: string) => {
                    const message = lastNameValidation(value);
                    return message || true;
                  },
                }}
              />
            </div>
            <div className={styles['body-group']}>
              <div onClick={() => onScrollToElement('sign-up-form-dob')}>
                <Scroll.Element name="sign-up-form-dob">
                  <FormTextInput
                    autoComplete="bday"
                    type="date"
                    name="dob"
                    label="Date of Birth"
                    control={control}
                    placeholder="MM/DD/YYYY"
                    mask="99/99/9999"
                    required="This field is required"
                    rules={{
                      validate: (value: string) => {
                        if (!moment(value, 'MM/DD/YYYY', true).isValid()) {
                          return 'Invalid date of birth format';
                        }
                      },
                    }}
                    onClick={() => setIsModalOpen(true)}
                  />
                </Scroll.Element>
              </div>
              <PhoneInput
                control={control}
                phoneNumber={signUp.phoneNumber}
                countryCode={signUp.countryCode}
                onChangeCountryCode={(countryCode, phoneNumber) => {
                  dispatch(
                    updateFBStep2({
                      signUp: {
                        countryCode,
                        phoneNumber,
                      } as SignUp,
                    })
                  );
                }}
              />
            </div>
          </div>
          <div className={styles['sign-in-section']}>
            <div className={styles['option-container']}>
              Already have account?{' '}
              <button
                type="button"
                className={styles['navigate-button']}
                onClick={handleClickOnBack}
              >
                Sign in
              </button>
            </div>
          </div>
          <div className={styles['footer-section']}>
            <DentalButton
              variant="outlined"
              onClick={() => dispatch(popFamilyStep())}
            >
              Back
            </DentalButton>
            <DentalButton
              type="button"
              disabled={!formState.isValid}
              variant="contained"
              isLoading={status === LOADING_STATUS.LOADING}
              onClick={handleOnSubmit}
            >
              Next
            </DentalButton>
          </div>
        </div>
      </form>
    </>
  );
};

export default SignUpForm;
